import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Fab,
  Modal,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import MenuIcon from '@mui/icons-material/Menu';
import { formatDate } from "../../utils/patientUtils";


const styles = {
  fab: {
    // Remove position fixed and related props
    borderRadius: "8px",
    background: "var(--Blue, #0D1E83)",
    color: "#FFFFFF",
    "&:hover": {
      background: "var(--Blue, #0D1E83)",
    },
    textTransform: 'none',
    margin: '16px auto',     // centers horizontally with some margin
    display: 'block',        // makes Fab behave like a block element (full width container centered by margin auto)
    width: '125px',
  },
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 300,
    maxHeight: "80vh", // Set max height
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 3,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
  },
  listContainer: {
    flexGrow: 1, // Allow list to expand
    overflowY: "auto", // Enable scrolling
    maxHeight: "60vh", // Set a fixed height for scrolling
  },
  listItemButton: {
    display: "flex",
    justifyContent: "space-between",
  },
};

const ReportButton = ({ reports }) => {
  const [open, setOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const { patientrecordId } = useParams(); // Get current report ID from URL
  const navigate = useNavigate();

  // Sync selectedReport with current URL parameter
  useEffect(() => {
    if (patientrecordId) {
      setSelectedReport(patientrecordId);
    } else if (reports && reports.length > 0) {
      // If no patientrecordId in URL, default to the latest report
      const sortedReports = [...reports].reverse();
      setSelectedReport(sortedReports[0].uuid);
    }
  }, [patientrecordId, reports]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => setOpen(false);
  
  const handleSelectReport = (reportUuid) => { 
    setSelectedReport(reportUuid);
    navigate(`/patientrecord/${reportUuid}`);
    handleClose();
  };

  const sortedReports = [...reports].reverse();

  // Find current report index for display
  const currentReportIndex = sortedReports.findIndex(report => report.uuid === selectedReport);
  const displayReportNumber = currentReportIndex !== -1 ? sortedReports.length - currentReportIndex : sortedReports.length;

  return (
    <>
      <Fab sx={styles.fab} onClick={handleOpen}>
        &nbsp;<MenuIcon />&nbsp; Report {displayReportNumber}
      </Fab>
      <Modal open={open} onClose={handleClose}>
        <Box sx={styles.modalBox}>
          <Box sx={styles.header}>
            <Typography variant="h6">All Reports</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List sx={styles.listContainer}>
            {sortedReports.map((report, index) => (
              <ListItem key={report.uuid} divider>
                <ListItemButton
                  sx={styles.listItemButton}
                  onClick={() => handleSelectReport(report.uuid)}
                >
                  <ListItemText
                    primary={`Report ${sortedReports.length - index}${index === 0 ? " (Latest Report)" : ""}`}
                    secondary={`Visit Date: ${formatDate(report.visited_at)}`}
                  />
                  {selectedReport === report.uuid && <CheckIcon color="primary" />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Modal>
    </>
  );
};

export default ReportButton;
